import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import '../HospitalDashboard/HospitalDashboard.css';
import './RequestCallback.css';
import moment from 'moment';
import { TIMELINE_CONST } from '../../common/constant/common-constants';
import { AssignHospital, getRequestCallback } from '../../helper/RequestCallbackHelper';
import { get_NX5_RequestCallback } from '../../helper/NX5/NX5_RequestCallbackHelper';
import { REQUEST_STAUS } from './STATUS_ENUM';
import { connect } from 'react-redux';
import { ROLE } from '../../enum/role';
import { ACTIVE_DEVICE } from '../../common/constant/common-constants';

const STATUS_FILTER = {
  ALL: 'all',
  PENDING: 'pending',
  RESOLVED: 'resolved',
};

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const isPendingStatus = (status) => String(status ?? '').toLowerCase() === 'pending';

const getStatusLabel = (status) => {
  const key = String(status ?? '').toUpperCase();
  return REQUEST_STAUS[key] ?? status ?? 'Unknown';
};

const getDisplaySerial = (machineUDI, isV730i) => {
  if (!machineUDI) return '—';
  if (isV730i) {
    const parts = String(machineUDI).split('-').filter((part) => part.includes('1G'));
    return parts.length ? parts.join('-') : machineUDI;
  }
  return machineUDI;
};

const getDisplayRequestId = (requestId, isV730i) => {
  if (!requestId) return '—';
  return isV730i ? requestId.slice(0, 15) : requestId;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  return `${moment(value).format(TIMELINE_CONST.DATE_FORMAT)} ${moment(value).format(TIMELINE_CONST.TIME_FORMAT)}`;
};

const matchesSearch = (item, query, isV730i) => {
  if (!query) return true;
  const q = query.toLowerCase();
  const fields = [
    item.requestId,
    item.phoneNumber,
    item.status,
    item.currentOsVersion,
    item.currentSoftwareVersion,
    item.machine_udi_registration?.machineUDI,
    getDisplaySerial(item.machine_udi_registration?.machineUDI, isV730i),
  ];
  return fields.some((field) => String(field ?? '').toLowerCase().includes(q));
};

const matchesVersionFilter = (item, versionQuery) => {
  if (!versionQuery) return true;
  const q = versionQuery.toLowerCase();
  const os = String(item.currentOsVersion ?? '').toLowerCase();
  const software = String(item.currentSoftwareVersion ?? '').toLowerCase();
  return os.includes(q) || software.includes(q);
};

const matchesDateFilter = (item, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  if (!item.createdAt) return false;

  const requestDate = moment(item.createdAt).startOf('day');
  if (dateFrom && requestDate.isBefore(moment(dateFrom).startOf('day'))) return false;
  if (dateTo && requestDate.isAfter(moment(dateTo).startOf('day'))) return false;
  return true;
};

const matchesStatusFilter = (item, filter) => {
  if (filter === STATUS_FILTER.ALL) return true;
  if (filter === STATUS_FILTER.PENDING) return isPendingStatus(item.status);
  if (filter === STATUS_FILTER.RESOLVED) return !isPendingStatus(item.status);
  return true;
};

const RequestCard = ({ request, isV730i, onOpenCms }) => {
  const machineUDI = request.machine_udi_registration?.machineUDI;
  const serial = getDisplaySerial(machineUDI, isV730i);
  const statusLabel = getStatusLabel(request.status);
  const isPending = isPendingStatus(request.status);

  const handleSerialClick = (event) => {
    event.stopPropagation();
    if (isV730i && machineUDI) {
      onOpenCms(machineUDI, ACTIVE_DEVICE.V730i);
    }
  };

  return (
    <article className="hospital-card hospital-card--static">
      <div className="hospital-card-head">
        <h3 className="hospital-card-name">{getDisplayRequestId(request.requestId, isV730i)}</h3>
        <span className={`hospital-status-badge ${isPending ? 'hospital-status-badge--inactive' : 'hospital-status-badge--active'}`}>
          {statusLabel}
        </span>
      </div>

      <div className="hospital-card-details">
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">SERIAL</span>
          {isV730i && machineUDI ? (
            <button type="button" className="rcb-serial-link" onClick={handleSerialClick}>
              {serial}
            </button>
          ) : (
            <span className="hospital-card-detail-val">{serial}</span>
          )}
        </div>
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">OS</span>
          <span className="hospital-card-detail-val">
            {isV730i ? displayValue(request.currentOsVersion) : '—'}
          </span>
        </div>
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">SYSTEM</span>
          <span className="hospital-card-detail-val">
            {isV730i ? displayValue(request.currentSoftwareVersion) : '—'}
          </span>
        </div>
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">MOBILE</span>
          <span className="hospital-card-detail-val">{displayValue(request.phoneNumber)}</span>
        </div>
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">REQUESTED</span>
          <span className="hospital-card-detail-val">{formatDateTime(request.createdAt)}</span>
        </div>
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">ATTENDED</span>
          <span className="hospital-card-detail-val">{formatDateTime(request.attendedAt)}</span>
        </div>
        {request.remark && (
          <div className="hospital-card-detail-row">
            <span className="hospital-card-detail-label">REMARK</span>
            <span className="hospital-card-detail-val hospital-card-detail-val--wrap">
              {request.remark}
            </span>
          </div>
        )}
      </div>
    </article>
  );
};

const RequestCallback = (props) => {
  const [dataContainer, setDataContainer] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [versionQuery, setVersionQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER.ALL);
  const [loading, setLoading] = useState(true);
  const searchInputRef = useRef(null);

  const isV730i = props.activeDevice === ACTIVE_DEVICE.V730i;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const api = isV730i
      ? getRequestCallback()
      : props.activeDevice === ACTIVE_DEVICE.NX5
        ? get_NX5_RequestCallback()
        : null;

    if (!api) {
      setLoading(false);
      return undefined;
    }

    api
      .then((res) => {
        if (cancelled) return;
        const data = isV730i ? res.data.machineServiceRequest : res.data;
        setDataContainer(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) console.log(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      cancelled = true;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [props.activeDevice, isV730i]);

  const openCMS = useCallback((machineUDI, activeDevice) => {
    if (!props.email) return;
    AssignHospital({
      machineUDI,
      email: props.email,
      activeDevice,
    }).then((res) => {
      if (res === 'Hospital ID updated successfully.') {
        window.open(process.env.REACT_APP_CMS_APP_URL, '_blank');
      } else {
        alert(res);
      }
    });
  }, [props.email]);

  const baseFilteredRequests = useMemo(() => {
    if (!Array.isArray(dataContainer)) return [];
    return dataContainer.filter(
      (item) =>
        matchesSearch(item, searchQuery, isV730i)
        && matchesVersionFilter(item, versionQuery)
        && matchesDateFilter(item, dateFrom, dateTo)
    );
  }, [dataContainer, searchQuery, versionQuery, isV730i, dateFrom, dateTo]);

  const filteredRequests = useMemo(
    () => baseFilteredRequests.filter((item) => matchesStatusFilter(item, statusFilter)),
    [baseFilteredRequests, statusFilter]
  );

  const stats = useMemo(() => ({
    total: baseFilteredRequests.length,
    pending: baseFilteredRequests.filter((item) => isPendingStatus(item.status)).length,
    resolved: baseFilteredRequests.filter((item) => !isPendingStatus(item.status)).length,
  }), [baseFilteredRequests]);

  if (!(props.userRole === ROLE.ADMIN || props.userRole === ROLE.SERVICE)) {
    return (
      <div>
        <h1>Unauthorized Access</h1>
        <p>You do not have the necessary permissions to access this page.</p>
      </div>
    );
  }

  return (
    <div className="hospital-page">
      <div className="rcb-header-bar">
        <h3 className="hospital-title">Machine Service Requests</h3>
        <div className="rcb-filters-actions">
          <label className="rcb-date-field">
            <span className="rcb-date-label">From</span>
            <input
              type="date"
              className="rcb-date-input"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(event) => setDateFrom(event.target.value)}
              aria-label="Filter from date"
            />
          </label>
          <label className="rcb-date-field">
            <span className="rcb-date-label">To</span>
            <input
              type="date"
              className="rcb-date-input"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(event) => setDateTo(event.target.value)}
              aria-label="Filter to date"
            />
          </label>
          <input
            type="text"
            className="hospital-search-input rcb-version-input"
            placeholder="Search OS or System version..."
            value={versionQuery}
            onChange={(event) => setVersionQuery(event.target.value)}
            aria-label="Filter by version"
          />
          <input
            ref={searchInputRef}
            type="text"
            className="hospital-search-input rcb-search-input"
            placeholder="Search SR ID, serial, mobile...  ⌘ K"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
      </div>

      <div className="rcb-stats-grid">
        <button
          type="button"
          className={`hospital-stat-card rcb-stat-card${statusFilter === STATUS_FILTER.ALL ? ' rcb-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(STATUS_FILTER.ALL)}
          aria-pressed={statusFilter === STATUS_FILTER.ALL}
        >
          <span className="hospital-stat-label">TOTAL REQUESTS</span>
          <span className="hospital-stat-value">{stats.total}</span>
        </button>
        <button
          type="button"
          className={`hospital-stat-card rcb-stat-card rcb-stat-card--pending${statusFilter === STATUS_FILTER.PENDING ? ' rcb-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(STATUS_FILTER.PENDING)}
          aria-pressed={statusFilter === STATUS_FILTER.PENDING}
        >
          <span className="hospital-stat-label">PENDING</span>
          <span className="hospital-stat-value">{stats.pending}</span>
        </button>
        <button
          type="button"
          className={`hospital-stat-card rcb-stat-card rcb-stat-card--resolved${statusFilter === STATUS_FILTER.RESOLVED ? ' rcb-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(STATUS_FILTER.RESOLVED)}
          aria-pressed={statusFilter === STATUS_FILTER.RESOLVED}
        >
          <span className="hospital-stat-label">RESOLVED</span>
          <span className="hospital-stat-value">{stats.resolved}</span>
        </button>
      </div>

      <div className="hospital-toolbar">
        <h2 className="hospital-section-title">Requests</h2>
      </div>

      <div className="hospital-cards-scroll">
        {loading ? (
          <div className="hospital-empty">Loading requests...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="hospital-empty">No service requests found.</div>
        ) : (
          <div className="hospital-cards-grid rcb-cards-grid">
            {filteredRequests.map((request) => (
              <RequestCard
                key={request.id ?? request.requestId ?? `${request.createdAt}-${request.phoneNumber}`}
                request={request}
                isV730i={isV730i}
                onOpenCms={openCMS}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  email: state.userDetails?.email,
  userRole: state.userDetails.userRole,
  activeDevice: state.setactiveDevice.device,
});

export default connect(mapStateToProps, null)(RequestCallback);
